import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { collection, getDocs, limit, orderBy, query, where } from '@/services/neonFirestore'
import { db } from '@/firebase/config'
import { useAuthStore } from '@/stores/auth'

const POLL_MS = 15000

export const pendingLeaveRequestCount = ref(0)

function leaveTypeLabel(type) {
  const raw = String(type || 'leave').replace(/[-_]/g, ' ')
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

function requestBody(request) {
  const name = request.employeeName || 'A staff member'
  const days = Number(request.days)
  const type = leaveTypeLabel(request.type)
  if (Number.isFinite(days)) {
    return `${name} requested ${type} leave for ${days} day${days === 1 ? '' : 's'}.`
  }
  return `${name} requested ${type} leave.`
}

export function useLeaveRequestAlerts() {
  const authStore = useAuthStore()
  const router = useRouter()
  const toast = useToast()
  let timer = null
  let seen = new Set()
  let primed = false
  let polling = false

  const canWatch = () => {
    return !!(
      authStore.isAuthenticated &&
      authStore.userProfile?.companyId &&
      (authStore.isManager || authStore.userProfile?.canManageLeaveRequests === true)
    )
  }

  const openLeaveManagement = () => {
    router.push('/leave-management')
  }

  const showPopup = (request) => {
    const title = 'New Leave Request'
    const body = requestBody(request)

    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden' &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        const notification = new Notification(title, {
          body,
          icon: '/pwa-192x192.png',
          tag: `leave_${request.id}`,
        })
        notification.onclick = () => {
          window.focus()
          openLeaveManagement()
          notification.close()
        }
        return
      } catch (error) {
        console.warn('Leave request notification failed, falling back to toast:', error)
      }
    }

    toast.info(`${title}: ${body}`, {
      timeout: 8000,
      onClick: openLeaveManagement,
    })
  }

  const poll = async () => {
    if (!canWatch() || polling) return
    polling = true
    try {
      const leaveQuery = query(
        collection(db, 'leaveRequests'),
        where('companyId', '==', authStore.userProfile.companyId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
        limit(100),
      )
      const snapshot = await getDocs(leaveQuery)
      const requests = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      pendingLeaveRequestCount.value = requests.length

      if (!primed) {
        requests.forEach((request) => seen.add(request.id))
        primed = true
        return
      }

      const currentUserId = authStore.user?.uid
      for (const request of requests) {
        if (seen.has(request.id)) continue
        seen.add(request.id)
        if (request.employeeId && request.employeeId === currentUserId) continue
        showPopup(request)
      }
    } catch (error) {
      console.warn('Leave request notification check failed:', error)
    } finally {
      polling = false
    }
  }

  const start = () => {
    if (timer) return
    void poll()
    timer = setInterval(() => {
      void poll()
    }, POLL_MS)
  }

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
    primed = false
    seen = new Set()
    pendingLeaveRequestCount.value = 0
  }

  const onVisible = () => {
    if (document.visibilityState === 'visible') void poll()
  }

  watch(
    () => [
      authStore.isAuthenticated,
      authStore.userProfile?.companyId,
      authStore.userProfile?.role,
      authStore.userProfile?.canManageLeaveRequests,
    ],
    () => {
      if (canWatch()) start()
      else stop()
    },
    { immediate: true },
  )

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisible)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisible)
    stop()
  })
}
